import React, { FC, useEffect, useState } from 'react';
import Image from 'components/image/Image';
import { industryItems } from 'core/constants/industryItems';
import CompetenciesBlock from 'components/shared/CompetenciesBlock/Index';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { CompetencieService } from 'services/competencies/competencies';
import { ISkills } from 'core/interface/skills';
import { ChartData } from 'chart.js';
import useLocales from 'hooks/useLocales';
import { CompetencyData } from 'core/interface/assessment';
import colors from 'assets/variables/colors';

import './Index.scss';

const TeaserViewPopup: FC = (): JSX.Element => {
  const { t } = useLocales();
  const [candidateView, setCandidateView] = useState<any>();
  const [displayData, setDisplayData] = useState<CompetencyData>();
  const [groups, setGroups] = useState<string[]>([]);
  const [group, setGroup] = useState<string>('');
  const [currentGroup, setCurrentGroup] = useState<CompetencyData>();

  const candidate = useSelector(
    (state: RootState) => state.candidate
  ).candidate;
  const languages = useSelector(
    (state: RootState) => state.languages
  ).candidate_language;
  const educations = useSelector(
    (state: RootState) => state.educations
  ).educations;
  const skills = useSelector((state: RootState) => state.skills).skills;
  const assessment = useSelector((state: RootState) => state.assessment);

  useEffect(() => {
    // Process assessment data
    if (assessment?.assessmentData?.[0]?.report?.competencies) {
      const result = assessment.assessmentData[0].report.competencies;
      const competencyGroups = Object.keys(result);

      setDisplayData(result);
      setGroups(competencyGroups);
      setGroup(competencyGroups[0]); // Set first group as default
    }
  }, [assessment]);

  useEffect(() => {
    if (displayData && group) {
      const currentGroupData = displayData[
        group as keyof typeof displayData
      ] as CompetencyData;
      setCurrentGroup(currentGroupData);
    }
  }, [displayData, group]);

  useEffect(() => {
    setCandidateView((prev: any) => ({
      ...prev,
      ...candidate,
      languages: languages?.map((item) => item.language.name).join(', '),
      educations: educations?.map((e) => e.study.name).join(', '),
      skills: skills?.map((skill: ISkills) => skill.skill.name).join(', ')
    }));
    getCompetencies();
  }, [candidate, languages, educations]);

  const getCompetencies = () => {
    CompetencieService.getCompetencies();
  };

  const generateChartData = (groupData: CompetencyData): ChartData => {
    return {
      labels: groupData?.lables || [],
      datasets: [
        {
          label: group,
          data: groupData?.data || [],
          backgroundColor: 'rgba(82, 37, 131, 0.50)',
          borderColor: colors.tenantColor,
          borderWidth: 1
        }
      ]
    };
  };

  // Generate descriptions for competencies
  const generateDescriptions = (groupData: CompetencyData): any[] => {
    if (!groupData?.lables) return [];

    // Create simple descriptions for each competency
    return groupData.lables.map((label: string, index: number) => ({
      name: label,
      description: `${label} competency description` // This would ideally come from your API
    }));
  };

  return (
    <div className="teaser">
      <div className="teaser__teaserTop">
        <div className="teaser__teaserTop__profile teaser-block">
          <Image
            className="teaser__teaserTop__profile-avatar"
            url={candidate?.avatar}
            width={115}
            height={115}
            objectFit="cover"
          />
          <span className="teaser__teaserTop__profile-role about-item">
            {candidate?.name}
          </span>
          {candidate?.pitch && (
            <p className="teaser__teaserTop__profile-about about-item">
              {`"${candidate?.pitch}"`}
            </p>
          )}
        </div>
        <div className="teaser__teaserTop__teaserIndustry teaser-block">
          {candidateView &&
            industryItems(candidateView).map((item, index) => (
              <div
                key={index}
                className="teaser__teaserTop__teaserIndustry__item"
              >
                <span className="teaser__teaserTop__teaserIndustry__item-img">
                  <Image
                    className="industry-image"
                    name={item.image}
                    width={14}
                    height={14}
                  />
                </span>
                <div className="teaser__teaserTop__teaserIndustry__item__cont">
                  <span className="teaser__teaserTop__teaserIndustry__item__cont-title">
                    {item.title}
                  </span>
                  <span className="teaser__teaserTop__teaserIndustry__item__cont-desc">
                    {item.desc}
                  </span>
                </div>
              </div>
            ))}
        </div>
      </div>
      {currentGroup ? (
        <div className="teaser__bottom">
          <div className="teaser__bottom__left">
            <div className="teaser__bottom__left__options">
              {groups.map((groupName, index) => (
                <div
                  key={index}
                  onClick={() => setGroup(groupName)}
                  className="teaser__bottom__left__options__option"
                >
                  <span className="teaser__bottom__left__options__option-dot"></span>
                  <span
                    className={`teaser__bottom__left__options__option-title ${
                      groupName === group ? 'active' : ''
                    }`}
                  >
                    {groupName}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="teaser__bottom__right">
            <CompetenciesBlock
              title={group}
              chartData={generateChartData(currentGroup)}
              descriptions={generateDescriptions(currentGroup)}
              noLabels
              showDescription={true}
              hideHeader={true}
              chartWidth={600}
              chartHeight={600}
            />
          </div>
        </div>
      ) : (
        <div className="no_assessment">
          <span className="no_assessment_text">
            {t('There are no assessment results.')}
          </span>
        </div>
      )}
    </div>
  );
};

export default TeaserViewPopup;
